import type { PrivateEvent, PrivateEventDetail } from '../../api/privateEvents';

/**
 * The display rules shared by the private-events table, its phone card list
 * and the detail page (BON-518, BON-525), so the layouts cannot drift into
 * telling different stories.
 *
 * Both rules carry real meaning and are not cosmetic:
 *
 *   - A field an imported event never had reads "Not recorded in FareHarbor",
 *     never an em-dash or "Not set". FareHarbor holds a location for only about
 *     half of the imported events, so blank is a FACT about the source rather
 *     than an omission by the RD, and the two must not look alike. A field left
 *     empty on an event made in Nebari is an ordinary gap and reads as one.
 *   - The deal value is always suffixed "est." wherever it is shown, because it
 *     is hand-entered and nothing reconciles it. It must never read like the
 *     settled revenue on workshop surfaces.
 */
/** What a to-be-determined location reads as, wherever it is shown. */
export const LOCATION_TBD = 'To be determined';

export function formatLocalDate(localStartsAt: string | null, startsAt: string): string {
  // `localStartsAt` is the venue wall clock with no zone. Slicing beats parsing:
  // `new Date('2026-11-12T18:00:00')` is interpreted in the BROWSER's zone,
  // which would shift the displayed time for anyone outside the venue's.
  const source = localStartsAt ?? startsAt;
  const [date, time] = source.split('T');
  const [y, m, d] = date.split('-');
  const hhmm = (time ?? '').slice(0, 5);
  return `${m}/${d}/${y.slice(2)}${hhmm ? ` · ${hhmm}` : ''}`;
}

export function formatDealValue(cents: number | null): string {
  if (cents === null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export function staffingLabel(event: PrivateEvent): { text: string; color: string } {
  return event.isStaffed
    ? { text: 'Staffed', color: 'var(--nb-primary)' }
    : { text: 'Needs crew', color: 'var(--nb-warn)' };
}

/** Where an event is, as one bold line and an optional quieter one. */
export interface EventLocation {
  primary: string;
  /** "City · client site" or "client site". Null for a partner venue. */
  subline: string | null;
}

/**
 * Deep link to a HubSpot deal. `hubspotDealId` is stored bare (the create form
 * strips any pasted URL down to the id), and building a record link needs a
 * portal id the app has no other source for, so `VITE_HUBSPOT_PORTAL_ID` is
 * optional. Null when it is unset — a guessed URL with no portal id in it is
 * not a working link, so the caller hides the affordance entirely rather than
 * offering a link that doesn't go anywhere.
 */
export function hubspotDealUrl(dealId: string): string | null {
  const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID as string | undefined;
  return portalId
    ? `https://app.hubspot.com/contacts/${portalId}/record/0-3/${encodeURIComponent(dealId)}`
    : null;
}

/** The fields every location rule reads — shared by the list row and the
 *  detail read, which carries a few more address fields besides. */
export type LocationFields = Pick<PrivateEvent, 'locationName' | 'city' | 'state' | 'venueName'>;

/**
 * Where the event is, or null when nothing at all was recorded — which only
 * happens to an event imported from FareHarbor, and is what the "not recorded"
 * treatment renders from.
 */
export function eventLocation(event: LocationFields): EventLocation | null {
  if (event.venueName) return { primary: event.venueName, subline: null };
  if (event.locationName) {
    return {
      primary: event.locationName,
      subline: event.city ? `${event.city} · client site` : 'client site',
    };
  }
  const place = [event.city, event.state].filter(Boolean).join(', ');
  return place ? { primary: place, subline: 'client site' } : null;
}

/** "a", "a and b", "a, b and c". */
export function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** What the list's completeness marker counts as missing. */
export function incompleteListFields(event: PrivateEvent): string[] {
  const missing: string[] = [];
  if (!eventLocation(event)) missing.push('location');
  if (!event.isStaffed) missing.push('crew');
  if (!event.hasContact) missing.push('contact');
  return missing;
}

/** What the detail page's import banner names as never recorded. */
export function missingImportFields(event: PrivateEventDetail): string[] {
  const missing: string[] = [];
  if (!eventLocation(event)) missing.push('the location');
  if (event.crew.length === 0) missing.push('the crew');
  if (!event.contactEmail) missing.push("the contact's email");
  return missing;
}
